import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import Div from '@/shared/Div';
import Container from '@/shared/Container';
import Heading from '@/shared/Heading';
import SkuCards from '@/shared/crisp/SkuCards';
import { getProductPrices } from '@/actions/ecom/productDetail';
import useTriggerRequest from '@/hooks/useTriggerRequest';
import is from '@/utils/is';
import dlv from 'dlv';
import Carousel, { CarouselItem } from '@/shared/crisp/Carousel';
import { UpsellMediumLoader } from '@/shared/crisp/Loaders/SkuCards';
import classNames from 'classnames';
import withContentFlags from '@/hoc/withContentFlags';

interface Product {
  id: string;
  attributes?: {
    materialId?: string;
    slug?: string;
    mrp?: number;
    sellingPrice?: number;
    discText?: string;
  };
}

interface TrendingProductsProps {
  title: string;
  type: string;
  products: {
    data: Product[];
  };
}

const TrendingProducts = ({ title, products }: TrendingProductsProps) => {
  const [prices, setPrices] = useState<any[]>([]);

  const materialIds = useMemo(
    () => products?.data?.map((product) => product.attributes?.materialId).filter(Boolean),
    [products?.data],
  );

  const fetchProductPrices = (...args: any) => getProductPrices.apply(null, args);
  const { promisifyTrigger, isLoading } = useTriggerRequest({ fetch: fetchProductPrices });

  const renderBanner = useCallback(
    (item: Product, index: number, isSnapPoint: boolean) => {
      const materialId = dlv(item, 'attributes.materialId');
      let copiedItem = Object.assign({}, item);

      const price = prices[materialId] ?? {};
      copiedItem = {
        ...copiedItem,
        attributes: {
          ...copiedItem.attributes,
          ...price,
        },
      };

      const data = dlv(copiedItem, 'attributes', {});
      return (
        <CarouselItem
          isSnapPoint={isSnapPoint}
          key={'trending-products' + data?.materialId + index}
          className="pl-6 last:pr-6 md:p-0 md:last:p-0"
        >
          {isLoading ? (
            <UpsellMediumLoader />
          ) : (
            <SkuCards
              dataTestId={`banner-${copiedItem.id}`}
              type="UpsellMedium"
              className="w-[250px] min-h-[390px]"
              data={data}
            />
          )}
        </CarouselItem>
      );
    },
    [prices, isLoading],
  );

  useEffect(() => {
    const fetch = async () => {
      const productPrices = await promisifyTrigger(materialIds.join(','));
      if (productPrices) setPrices(productPrices);
    };
    if (materialIds) {
      fetch();
    }
  }, [materialIds]);

  if (is.empty.array(products?.data)) return null;
  return (
    <Div className="py-s8 md:py-s9 bg-cs-secondary">
      <Container className="flex flex-col gap-s6">
        <Heading level={3} className="text-h-1 md:text-hd-1 text-cm-primary">
          {title}
        </Heading>
        <Carousel
          list={products?.data}
          render={renderBanner}
          className={classNames('md:gap-s6 -mx-6 md:m-0')}
        />
      </Container>
    </Div>
  );
};

export default withContentFlags(memo(TrendingProducts));
